import router from '@/router';
import { useUserStore } from 'd3components';
import { addDynamicRoutes } from '@/router';
import { toast } from 'd3components/src/utils/tools';
import { getKeyStr ,removeKey, setKey, setKeyJsonStr ,getKeyJson,updateKeyJson,getsubKey, removeKeys} from 'd3components/src/utils/localStorage';

import { statusBarAdapter } from '@/capacitor/statusbar/statusbaradapter';
import { Capacitor } from '@capacitor/core';
import { i18n } from '@/i18n';
import { getJumpviewRouteLocation } from '@/utils/jumpview';

const t = i18n.global.t;

// ==================== 常量配置 ====================

/** 白名单路由：无需登录即可访问的页面 */
const WHITE_LIST = [
  '/login',
  '/register',
  '/forgotPassword',
  '/ad',
  '/serve',
  '/privacy',
  '/protocol',
  '/default-privacy',
  '/default-protocol',
  '/version-history',
  '/download',
];

// ==================== 工具函数 ====================

/**
 * 检查token是否过期
 * @param auth 认证信息对象
 * @returns true表示已过期，false表示未过期
 */
const isTokenExpired = (auth: any) => auth?.accessExpireAt && new Date(auth.accessExpireAt).getTime() <= Date.now();

/**
 * 更新原生平台状态栏样式
 * @param to 目标路由对象
 */
const updateCapacitorStatusBar = (to: any) => {
  if (Capacitor.isNativePlatform()) {
    statusBarAdapter.setStatusBarForRoute(to.path).catch(() => { });
  }
};

// ==================== 认证相关 ====================

/**
 * 初始化用户认证信息
 * 优先使用store中的auth，如果没有则尝试从localStorage恢复
 * @param userStore 用户store实例
 * @returns 认证信息对象
 */
const initAuth = (userStore: any) => {
  let auth = userStore.auth;
  if (!auth || !Object.keys(auth).length) {
    try {
      // 检查uinfo数据完整性，防止后续解构错误
      const uinfo = userStore.uinfo;
      if (!uinfo || typeof uinfo !== 'object' || !uinfo.unid) {
        console.warn('uinfo数据不完整或已损坏，清理本地数据');
        userStore.auth = undefined;
        removeKey('auth');
        removeKey('uinfo');
        userStore.uinfo = {};
        return null;
      }

      userStore.initUserStore?.();
      auth = userStore.auth || getKeyJson('auth');
      if (auth && Object.keys(auth).length) {
        userStore.auth = auth;
      }
    } catch (error) {
      console.warn('初始化认证信息失败，可能数据已损坏:', error);
      // 清理损坏的数据
      userStore.auth = undefined;
      removeKey('auth');
      removeKey('uinfo');
      userStore.uinfo = {};
      return null;
    }
  }
  return auth;
};

// ==================== 路由加载 ====================

/**
 * 加载菜单数据并注册动态路由
 * @param userStore 用户store实例
 * @param force 是否强制重新注册路由
 * @returns 成功返回true，失败返回false
 */
const loadMenuAndRoutes = async (userStore: any, force = false) => {
  try {
    // 在调用firstMenuFun之前，再次验证uinfo数据完整性
    const uinfo = userStore.uinfo;
    if (!uinfo || typeof uinfo !== 'object' || !uinfo.unid) {
      console.warn('uinfo数据不完整，无法加载菜单');
      userStore.auth = undefined;
      removeKey('auth');
      removeKey('uinfo');
      userStore.uinfo = {};
      return false;
    }

    if (!userStore.routerData?.length || force) {
      await userStore.firstMenuFun();
    }
    if (userStore.routerData?.length) {
      await addDynamicRoutes(force);
      return true;
    }
    return false;
  } catch (error) {
    console.error('加载菜单和路由失败:', error);
    // 如果是因为数据损坏导致的错误，清理数据
    if (error instanceof TypeError && (
      error.message.includes('Cannot destructure') ||
      error.message.includes('Cannot read property') ||
      error.message.includes('Cannot read properties')
    )) {
      console.warn('检测到数据损坏（解构错误），清理本地数据');
      userStore.auth = undefined;
      removeKey('auth');
      removeKey('uinfo');
      userStore.uinfo = {};
    }
    return false;
  }
};

/**
 * 获取用户的首页路由路径
 * 根据菜单结构递归查找第一个可访问的页面
 * @param userStore 用户store实例
 * @returns 首页路由路径，默认为根路径
 */
const getFirstRoute = (userStore: any) => {
  const join = (base = '', seg: any) => {
    if (!seg) return base || '/';
    const path = seg?.startsWith?.('/') ? seg : `${base}/${seg}`;
    return path.replace(/\/+/g, '/');
  };

  /**
   * 深度优先搜索第一个有效路径
   * @param nodes 节点数组
   * @param base 父路径
   * @returns 有效路径或 null
   */
  const findFirst = (nodes: any[], base = ''): string | null => {
    for (const node of nodes) {
      if (!node) continue;

      const currentPath = join(base, node.path);

      // 如果是分组节点，递归查找其子节点
      if (node.meta?._isGroup && Array.isArray(node.children)) {
        const firstChildResult = findFirst(node.children, currentPath);
        if (firstChildResult) return firstChildResult;
        continue;
      }

      // 处理重定向
      if (node.redirect) {
        return join(base, node.redirect);
      }

      // 找到有效页面（非组节点且有路径）
      if (node.path) {
        return currentPath;
      }
    }
    return null;
  };

  const menuRouter = userStore.userMenuRouter;
  if (!menuRouter?.length) return '/';

  const firstRoute = findFirst(menuRouter);
  return firstRoute || '/';
};

// ==================== 路由守卫处理函数 ====================

/**
 * 处理临时token访问
 * 用于特殊场景：通过URL参数携带临时token访问特定页面
 * @param to 目标路由对象
 * @returns true表示是临时token访问，false表示不是
 */
const handleTempTokenAccess = (to: any) => {
  return to.query.tempToken && to.query.tempPath === to.path;
};

/**
 * 处理白名单路由访问
 * 规则：
 * 1. 未登录用户：允许访问
 * 2. 已登录用户：
 *    - token过期：清理数据，允许访问（用户需要重新登录）
 *    - token有效：重定向到首页（防止已登录用户访问登录页等）
 * @param to 目标路由对象
 * @param auth 认证信息
 * @param userStore 用户store实例
 * @returns false表示不是白名单路由，true表示放行，字符串表示需要重定向的路径
 */
const handleWhiteListAccess = async (to: any, auth: any, userStore: any) => {
  if (!WHITE_LIST.includes(to.path)) return false;

  // 未登录用户，直接放行
  if (!auth || !Object.keys(auth).length) return true;

  // 已登录用户，检查token是否过期
  if (isTokenExpired(auth)) {
    // token过期，清理数据，允许访问登录页
    userStore.logout();
    return true;
  }

  // token有效，尝试加载菜单（强制刷新）并重定向到首页
  if (await loadMenuAndRoutes(userStore, true)) {
    const jumpview = getJumpviewRouteLocation(router, to.query);
    if (jumpview) {
      console.log('[Guard] 已登录访问jumpview，跳转:', jumpview);
      return jumpview;
    }

    const firstRoute = getFirstRoute(userStore);
    console.log('[Guard] 白名单访问且已登录，首页面:', firstRoute);
    if (firstRoute && firstRoute !== to.path) {
      return firstRoute; // 返回重定向路径
    }
  }

  // 如果菜单加载失败，也允许访问（可能是网络问题）
  return true;
};

/**
 * 处理已登录用户的路由访问
 * 规则：
 * 1. token过期：跳转登录页
 * 2. 菜单加载失败：跳转登录页
 * 3. 访问根路径：跳转到首页
 * 4. 访问不存在的路由：跳转到首页
 * 5. 正常访问：放行
 * @param to 目标路由对象
 * @param auth 认证信息
 * @param userStore 用户store实例
 * @returns null表示放行，字符串表示需要重定向的路径
 */
const handleAuthenticatedAccess = async (to: any, auth: any, userStore: any) => {
  if (isTokenExpired(auth)) {
    toast(t('common.loginExpired'), 3);
    userStore.logout();
    return `/login?redirect=${encodeURIComponent(to.fullPath)}`;
  }

  if (!(await loadMenuAndRoutes(userStore))) {
    toast(t('common.menuLoadFailed'), 3);
    return '/login';
  }

  if (to.path === '/') {
    const firstRoute = getFirstRoute(userStore);
    console.log('[Guard] 访问根路径，重定向到首页面:', firstRoute);
    if (firstRoute && firstRoute !== '/') {
      return firstRoute;
    }
    return null;
  }

  // 动态路由刚注册，但 to 是在注册前解析的，可能未匹配
  // 重新 resolve 检查路由是否现在可达
  if (!to.matched.length) {
    const resolved = router.resolve(to.fullPath);
    if (resolved.matched.length > 0) {
      // 路由在动态注册后已可达，重新导航以正确解析
      console.log(`[Guard] 动态路由已注册，重新导航: ${to.fullPath}`);
      return to.fullPath;
    }
    // 确实不存在的路由，跳转首页
    const firstRoute = getFirstRoute(userStore);
    console.log(`[Guard] 路由不存在: ${to.path}, 尝试跳转首页面: ${firstRoute}`);
    if (firstRoute && firstRoute !== to.path) {
      return firstRoute;
    }
    console.warn(`无法找到路由: ${to.path} 且首页面查得相同，停止跳转`);
    return null;
  }

  return null; // 放行
};

/**
 * 处理未登录用户的自动登录尝试
 * 尝试从localStorage恢复认证信息并自动登录
 * @param to 目标路由对象
 * @param userStore 用户store实例
 * @returns 重定向路径（成功则跳转到目标页面，失败则跳转到登录页）
 */
const handleAutoLogin = async (to: any, userStore: any) => {
  const storedAuth = getKeyJson('auth');
  if (!storedAuth || isTokenExpired(storedAuth)) {
    return `/login?redirect=${encodeURIComponent(to.fullPath)}`;
  }

  userStore.auth = storedAuth;
  if (await loadMenuAndRoutes(userStore)) {
    return to.fullPath;
  }

  return `/login?redirect=${encodeURIComponent(to.fullPath)}`;
};

// ==================== 路由守卫 ====================

/**
 * 全局前置路由守卫
 * 处理流程：
 * 1. 更新状态栏样式（原生平台）
 * 2. 初始化认证信息
 * 3. 检查临时token访问
 * 4. 检查白名单访问
 * 5. 处理已登录用户访问
 * 6. 处理未登录用户自动登录
 */
router.beforeEach(async (to, _from, next) => {
  try {
    updateCapacitorStatusBar(to);

    const userStore = useUserStore();
    const auth = initAuth(userStore);

    // 临时token访问
    if (handleTempTokenAccess(to)) {
      next();
      return;
    }

    // 白名单处理
    const whiteListResult = await handleWhiteListAccess(to, auth, userStore);
    if (whiteListResult !== false) {
      if (typeof whiteListResult === 'string' || typeof whiteListResult === 'object') {
        next(typeof whiteListResult === 'string'
          ? { path: whiteListResult, replace: true }
          : { ...whiteListResult, replace: true });
      } else {
        next();
      }
      return;
    }

    // 已登录用户
    if (auth && Object.keys(auth).length) {
      const redirectPath = await handleAuthenticatedAccess(to, auth, userStore);
      if (redirectPath) {
        next({ path: redirectPath, replace: to.path === '/' });
      } else {
        next();
      }
      return;
    }

    // 未登录，尝试自动登录
    const autoLoginPath = await handleAutoLogin(to, userStore);
    next({ path: autoLoginPath, replace: true });
  } catch (error) {
    console.error('路由守卫执行失败:', error);

    // 清理可能损坏的数据
    const userStore = useUserStore();
    userStore.auth = undefined;
    userStore.logout?.(); // 调用logout清理所有相关数据

    // 如果当前不在登录页，跳转到登录页
    if (to.path !== '/login') {
      next({ path: '/login', replace: true });
    } else {
      next(); // 已经在登录页，直接放行
    }
  }
});

/**
 * 全局后置路由守卫
 * 路由切换完成后更新状态栏样式（原生平台）
 */
router.afterEach((to) => {
  if (Capacitor.isNativePlatform()) {
    setTimeout(() => {
      statusBarAdapter.setStatusBarForRoute(to.path).catch(() => { });
    }, 50);
  }
});

// ==================== 应用启动初始化 ====================

/**
 * 尝试加载用户菜单和动态路由
 * @param userStore 用户store实例
 * @returns 是否成功加载
 */
const tryLoadUserRoutes = async (userStore: any): Promise<boolean> => {
  try {
    if (!userStore.menuTree || userStore.menuTree.length === 0) {
      await userStore.firstMenuFun();
    }

    const routerData = (userStore as any).routerData;
    if (Array.isArray(routerData) && routerData.length > 0) {
      await addDynamicRoutes();
      return true;
    }

    return false;
  } catch (error) {
    console.warn('加载用户路由失败:', error);
    return false;
  }
};

/**
 * 从本地存储恢复认证信息
 * @returns 有效的认证信息，如果无效则返回null
 */
const restoreAuthFromStorage = (): any => {
  const storedAuth = getKeyJson('auth');
  if (!storedAuth || !(storedAuth as any).accessExpireAt) {
    return null;
  }

  const expireTime = new Date((storedAuth as any).accessExpireAt).getTime();
  if (expireTime <= Date.now()) {
    return null;
  }

  return storedAuth;
};

/**
 * 应用启动时的认证和路由初始化
 * 
 * 在应用启动时调用，尝试恢复用户的登录状态和加载动态路由
 * 
 * 执行流程：
 * 1. 检查数据完整性（auth和uinfo必须同时存在且有效）
 * 2. 检查store中是否有有效的认证信息
 *    - 如果有：加载菜单和动态路由
 * 3. 如果store中没有认证信息
 *    - 尝试从localStorage恢复
 *    - 如果恢复成功：加载菜单和动态路由
 * 4. 如果以上都失败：静默失败，等待用户登录
 * 
 * @returns Promise<void>
 */
export const bootstrapAuthAndRoutes = async (): Promise<void> => {
  try {
    const userStore = useUserStore();

    // 预检查：验证localStorage数据完整性
    const storedAuth = getKeyJson('auth');
    const storedUinfo = userStore.uinfo;

    // 如果auth存在但uinfo不存在或不完整，清理数据
    if (storedAuth && (!storedUinfo || typeof storedUinfo !== 'object' || !storedUinfo.unid)) {
      console.warn('检测到数据不一致（auth存在但uinfo损坏），清理数据');
      userStore.auth = undefined;
      removeKey('auth');
      removeKey('uinfo');
      userStore.uinfo = {};
      return;
    }

    // 场景1：store中已有认证信息
    if (userStore.auth && Object.keys(userStore.auth).length > 0) {
      await tryLoadUserRoutes(userStore);
      return;
    }

    // 场景2：尝试从本地存储恢复认证
    const restoredAuth = restoreAuthFromStorage();
    if (restoredAuth) {
      userStore.auth = restoredAuth;
      await tryLoadUserRoutes(userStore);
    }
  } catch (error) {
    console.warn('启动时初始化失败:', error);
    // 清理可能损坏的数据
    removeKey('auth');
    removeKey('uinfo');
    useUserStore().uinfo = {};
  }
};
